import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, Play, Check, Smartphone, Monitor, Users, 
  Shield, Calendar, FileText, Bell, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import useABTest from '../../hooks/useABTest';

const HeroSection = ({ heroContent, openBooking, openContact }) => {
  const [activeScreen, setActiveScreen] = useState('dashboard');
  const [isAnimating, setIsAnimating] = useState(false);
  
  // A/B Testing for hero headline
  const { variant: abVariant, trackConversion, isInTest } = useABTest('home', 'hero_headline');

  const screens = ['dashboard', 'participants', 'roster', 'incidents'];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveScreen(prev => {
          const currentIndex = screens.indexOf(prev);
          return screens[(currentIndex + 1) % screens.length];
        });
        setIsAnimating(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const screenContent = {
    dashboard: {
      label: 'Dashboard',
      color: 'care-teal',
      stats: [
        { label: 'Participants', value: '247', change: '+12%' },
        { label: 'Active Staff', value: '89', change: '+5%' },
        { label: 'Compliance', value: '98%', change: '+2%' }
      ]
    },
    participants: {
      label: 'Participants',
      color: 'tech-blue',
      items: ['Sarah Mitchell - Care Plan Due', 'James Cooper - New Assessment', 'Emma Wilson - Goal Review']
    },
    roster: {
      label: 'Rostering',
      color: 'heritage-orange',
      shifts: ['Morning Shift - 6 Staff', 'Afternoon - 8 Staff', 'Night Shift - 4 Staff']
    },
    incidents: {
      label: 'Incidents',
      color: 'purple-500',
      alerts: ['Low Risk - 2 Open', 'Medium Risk - 1 Review', 'Resolved Today - 5']
    }
  };

  return (
    <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-primary-navy via-[#0a1f3d] to-primary-navy overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Gradient Orbs */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-tech-blue/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-care-teal/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-heritage-orange/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
        
        {/* Floating Elements */}
        <div className="absolute top-1/4 left-[10%] w-4 h-4 bg-care-teal/50 rounded-full animate-bounce" style={{ animationDuration: '3s' }}></div>
        <div className="absolute top-1/3 right-[15%] w-3 h-3 bg-tech-blue/50 rounded-full animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
        <div className="absolute bottom-1/4 left-[20%] w-2 h-2 bg-heritage-orange/50 rounded-full animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6 animate-fadeIn">
              <span className="w-2 h-2 bg-care-teal rounded-full mr-2 animate-pulse"></span>
              <span className="text-sm font-medium text-white/90">AI-Powered NDIS Platform</span>
              <span className="mx-2 text-white/40">•</span>
              <span className="text-sm text-care-teal font-semibold">Australian Made</span>
            </div>
            
            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-headline">
              {abVariant?.headline ? (
                <>
                  <span className="block">{abVariant.headline}</span>
                  {abVariant.subheadline && (
                    <span className="block mt-2">
                      <span className="bg-gradient-to-r from-care-teal via-tech-blue to-care-teal bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                        {abVariant.subheadline.length > 50 ? '' : abVariant.subheadline}
                      </span>
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="block">{heroContent?.hero_headline?.split('.')[0] || 'Own Your NDIS Software'}.</span>
                  <span className="block mt-2">
                    <span className="bg-gradient-to-r from-care-teal via-tech-blue to-care-teal bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                      Stop Renting It.
                    </span>
                  </span>
                </>
              )}
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg md:text-xl text-white/70 mb-8 max-w-xl mx-auto lg:mx-0">
              {abVariant?.subheadline && abVariant.subheadline.length > 50 
                ? abVariant.subheadline 
                : heroContent?.hero_subheadline || 'GetCare 360 delivers a custom AI-powered NDIS platform built specifically for your Australian organisation—with one-time pricing and no per-user fees.'}
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8">
              {[
                { icon: Shield, text: 'NDIS Compliant' },
                { icon: Users, text: '100+ Providers' },
                { icon: Check, text: 'Australian Support' }
              ].map((badge, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 hover:bg-white/10 hover:border-care-teal/50 hover:scale-105 transition-all duration-300 cursor-default group"
                >
                  <badge.icon size={14} className="text-care-teal group-hover:scale-110 transition-transform" />
                  <span className="text-sm text-white/80 group-hover:text-white transition-colors">{badge.text}</span>
                </div>
              ))}
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                onClick={() => {
                  // Track A/B test conversion before opening booking
                  if (isInTest) trackConversion('click');
                  openBooking();
                }}
                className="bg-care-teal hover:bg-care-teal/90 text-white px-8 py-6 text-lg group shadow-lg shadow-care-teal/30 hover:shadow-care-teal/50 transition-all"
                data-testid="hero-cta-primary"
              >
                <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
                Book a Demo
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button 
                onClick={() => {
                  if (isInTest) trackConversion('click');
                  openContact();
                }}
                variant="outline" 
                className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-6 text-lg transition-all"
                data-testid="hero-cta-secondary"
              >
                Contact Us
              </Button>
            </div>
          </div>

          {/* Right - Interactive App Mockup */}
          <div className="relative hidden lg:block">
            {/* Desktop App */}
            <div className="relative">
              {/* Browser Window */}
              <div className="bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-white/10">
                {/* Browser Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-white/10">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-slate-700 rounded-lg px-4 py-1.5 text-sm text-white/60 flex items-center gap-2 max-w-xs mx-auto">
                      <Monitor size={14} />
                      app.getcare360.com.au
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Bell size={16} className="text-white/40" />
                    <div className="w-6 h-6 rounded-full bg-care-teal/30"></div>
                  </div>
                </div>

                {/* App Content */}
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 min-h-[320px]">
                  {/* Tab Navigation */}
                  <div className="flex gap-2 mb-6">
                    {screens.map((screen) => (
                      <button
                        key={screen}
                        onClick={() => setActiveScreen(screen)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          activeScreen === screen
                            ? 'bg-care-teal text-white'
                            : 'bg-white/5 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        {screenContent[screen].label}
                      </button>
                    ))}
                  </div>

                  {/* Screen Content */}
                  <div className={`transition-all duration-300 ${isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                    {activeScreen === 'dashboard' && (
                      <div className="grid grid-cols-3 gap-4">
                        {screenContent.dashboard.stats.map((stat, idx) => (
                          <div key={idx} className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10">
                            <p className="text-white/50 text-xs mb-1">{stat.label}</p>
                            <div className="flex items-end gap-2">
                              <span className="text-2xl font-bold text-white">{stat.value}</span>
                              <span className="text-care-teal text-xs font-medium">{stat.change}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeScreen === 'participants' && (
                      <div className="space-y-3">
                        {screenContent.participants.items.map((item, idx) => (
                          <div key={idx} className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 flex items-center justify-between group hover:bg-white/10 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-tech-blue/30 flex items-center justify-center">
                                <Users size={18} className="text-tech-blue" />
                              </div>
                              <span className="text-white/80 text-sm">{item}</span>
                            </div>
                            <ChevronRight size={18} className="text-white/30 group-hover:text-white/60 transition-colors" />
                          </div>
                        ))}
                      </div>
                    )}

                    {activeScreen === 'roster' && (
                      <div className="space-y-3">
                        {screenContent.roster.shifts.map((shift, idx) => (
                          <div key={idx} className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-heritage-orange/30 flex items-center justify-center">
                                  <Calendar size={18} className="text-heritage-orange" />
                                </div>
                                <span className="text-white/80 text-sm">{shift}</span>
                              </div>
                              <div className="flex -space-x-2">
                                {[...Array(3)].map((_, i) => (
                                  <div key={i} className="w-6 h-6 rounded-full bg-white/20 border-2 border-slate-800"></div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeScreen === 'incidents' && (
                      <div className="space-y-3">
                        {screenContent.incidents.alerts.map((alert, idx) => (
                          <div key={idx} className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 flex items-center gap-4">
                            <div className={`w-3 h-3 rounded-full ${idx === 0 ? 'bg-green-500' : idx === 1 ? 'bg-yellow-500' : 'bg-care-teal'}`}></div>
                            <span className="text-white/80 text-sm">{alert}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Progress Indicator */}
                  <div className="flex gap-2 mt-6 justify-center">
                    {screens.map((screen) => (
                      <div
                        key={screen}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          activeScreen === screen ? 'w-8 bg-care-teal' : 'w-2 bg-white/20'
                        }`}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Mobile Phone */}
              <div className="absolute -right-8 -bottom-8 w-44 animate-float">
                <div className="bg-slate-900 rounded-[2rem] p-2 shadow-2xl border-4 border-slate-700">
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-[1.5rem] p-4 aspect-[9/16] relative">
                    {/* Status Bar */}
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-white/60 text-[10px]">9:41</span>
                      <div className="flex gap-1 items-center">
                        <div className="w-4 h-2 bg-care-teal rounded-sm"></div>
                      </div>
                    </div>

                    {/* Mobile Content */}
                    <div className="space-y-2">
                      <div className="bg-care-teal/20 rounded-lg p-3 border border-care-teal/30">
                        <div className="flex items-center gap-2">
                          <Check className="text-care-teal" size={14} />
                          <span className="text-white/80 text-[10px]">Shift Started</span>
                        </div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-3">
                        <div className="h-2 w-full bg-white/20 rounded mb-1.5"></div>
                        <div className="h-2 w-3/4 bg-white/10 rounded"></div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full bg-tech-blue/30"></div>
                          <div className="flex-1">
                            <div className="h-1.5 w-16 bg-white/30 rounded"></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Label */}
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                      <div className="bg-white/10 backdrop-blur rounded-full px-3 py-1 flex items-center gap-1">
                        <Smartphone size={10} className="text-white/60" />
                        <span className="text-white/60 text-[8px]">Mobile App</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Stats Card */}
              <div className="absolute -left-12 top-1/2 -translate-y-1/2 bg-white p-4 rounded-xl shadow-xl border border-border-line animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-care-teal/10 flex items-center justify-center">
                    <Shield className="text-care-teal" size={20} />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-care-teal">98%</div>
                    <div className="text-xs text-text-secondary">Compliance</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
